import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { HoverCardSuppressedContext } from '../tasks/TaskHoverCard';

/**
 * Drag a calendar item to another day (Andrei, 2026-11-03). The task grids and
 * the posts calendar share it; each brings what it moves and how to save it.
 *
 * Desktop layouts only: on a phone the same items are scrollable agenda rows,
 * where a drag would fight the scroll. No keyboard sensor either, because
 * Enter on a chip has to keep opening it; the keyboard route to a new date is
 * the task's own date field.
 */
export function CalendarDnd<T>({
  onMove,
  renderOverlay,
  children,
}: {
  /** Fired only when the item lands on a different day from the one it left. */
  onMove: (item: T, dayKey: string) => void;
  renderOverlay: (item: T) => ReactNode;
  children: ReactNode;
}) {
  const [active, setActive] = useState<T | null>(null);
  // A few pixels of travel before it counts as a drag, so a click still opens.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function handleStart(event: DragStartEvent) {
    setActive((event.active.data.current?.item as T | undefined) ?? null);
  }

  function handleEnd(event: DragEndEvent) {
    setActive(null);
    const data = event.active.data.current;
    const target = event.over?.id;
    if (!data || typeof target !== 'string' || target === data.dayKey) return;
    onMove(data.item as T, target);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleStart}
      onDragEnd={handleEnd}
      onDragCancel={() => setActive(null)}
    >
      {/* A hover card over the drop targets would only be in the way. */}
      <HoverCardSuppressedContext.Provider value={active !== null}>
        {children}
        {/* Portalled: the overlay is position:fixed, and the calendar sits
            inside an animated (transformed) wrapper that would offset it. */}
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {active !== null ? (
              <div className="cursor-grabbing rounded opacity-90 shadow-lg">
                {renderOverlay(active)}
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </HoverCardSuppressedContext.Provider>
    </DndContext>
  );
}

export function DraggableCalendarItem<T>({
  id,
  item,
  dayKey,
  disabled,
  children,
}: {
  id: string;
  item: T;
  /** The day the item sits on now; dropping it back there is a no-op. */
  dayKey: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id,
    disabled,
    data: { item, dayKey },
  });
  return (
    <div
      ref={setNodeRef}
      className={isDragging ? 'opacity-40' : undefined}
      onPointerDown={(event) => {
        // React bubbles events through portals, so a press inside the chip's
        // hover card or one of its dropdowns reaches here too. Those are not
        // the chip: without this, selecting text in the card dragged the task.
        if (!event.currentTarget.contains(event.target as Node)) return;
        listeners?.onPointerDown?.(event);
      }}
    >
      {children}
    </div>
  );
}

export function DroppableCalendarDay({
  dayKey,
  className = '',
  children,
}: {
  dayKey: string;
  className?: string;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dayKey });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'ring-2 ring-inset ring-brand-400' : ''}`}
    >
      {children}
    </div>
  );
}
